<template>
  <DataTable 
    :value="value" 
    :showHeaders="false"
    class="compact-datatable"
    :tableStyle="tableStyle">
    <Column field="prop" :style="propColumnStyle">
      <template #body="slotProps">
        <strong>{{ slotProps.data.prop }}:</strong>
      </template>
    </Column>
    <Column field="value" :style="valueColumnStyle">
      <template #body="slotProps">
        <span :class="valueClass" :data-testid="slotProps.data.testId">{{ slotProps.data.value }}</span>
        <button
          v-if="slotProps.data.copyable && hasCopyableValue(slotProps.data)"
          type="button"
          class="compact-copy-btn"
          :title="t('Copiar')"
          :aria-label="t('Copiar')"
          :data-testid="slotProps.data.testId ? slotProps.data.testId + '-copy' : undefined"
          @click="copyRow(slotProps.data)"
        >
          <i class="pi pi-copy" />
        </button>
      </template>
    </Column>
  </DataTable>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';

interface CompactRow {
  prop: string;
  value: string | number | null;
  /**
   * Optional kebab-case test id stamped on the VALUE cell, so a caller can
   * address one field instead of matching loose text inside the table.
   * Omitted rows render no attribute at all.
   */
  testId?: string;
  /**
   * When true, a copy-to-clipboard button renders after the value (only when
   * the value is a real one — never for '—'/empty). The component performs
   * the clipboard write and emits `copied`/`copy-failed` with the row; the
   * TOAST belongs to the caller (same split PaymentLinksModal uses), because
   * a grid component announcing global messages would couple it to eventBus.
   */
  copyable?: boolean;
}

// Props
const props = defineProps<{
  value: CompactRow[];
  tableStyle?: string;
  propColumnWidth?: string;
  valueColumnWidth?: string;
  valueClass?: string;
}>();

const emit = defineEmits<{
  copied: [row: CompactRow];
  'copy-failed': [row: CompactRow];
}>();

const { t } = useI18n();

function hasCopyableValue(row: CompactRow): boolean {
  const v = row.value;
  return v !== null && v !== undefined && String(v).trim() !== '' && String(v) !== '—';
}

async function copyRow(row: CompactRow): Promise<void> {
  try {
    await navigator.clipboard.writeText(String(row.value));
    emit('copied', row);
  } catch {
    // Clipboard is permission-gated; the caller decides how to surface the
    // failure (emit, never silent-drop — the listener always hears one of the
    // two outcomes).
    emit('copy-failed', row);
  }
}

// Computed styles
const propColumnStyle = computed(() => `width: ${props.propColumnWidth ?? '40%'}`);
const valueColumnStyle = computed(() => `width: ${props.valueColumnWidth ?? '60%'}`);
</script>

<style scoped>
/* Compact DataTable styling */
.compact-datatable {
  margin-top: 8px;
}

.compact-datatable :deep(.p-datatable) {
  border: none;
}

.compact-datatable :deep(.p-datatable-tbody > tr) {
  border-bottom: 1px solid #f0f0f0;
}

.compact-datatable :deep(.p-datatable-tbody > tr:last-child) {
  border-bottom: none;
}

.compact-datatable :deep(.p-datatable-tbody > tr > td) {
  padding: 6px 8px;
  font-size: 0.85rem;
  line-height: 1.3;
}

.compact-datatable :deep(.p-datatable-tbody > tr:hover) {
  background-color: #f9f9f9;
}

.compact-copy-btn {
  margin-left: 6px;
  padding: 1px 4px;
  border: none;
  background: transparent;
  color: #6c757d;
  cursor: pointer;
  font-size: 0.8rem;
  vertical-align: middle;
}

.compact-copy-btn:hover {
  color: #212529;
}

.compact-copy-btn:focus-visible {
  outline: 2px solid #6c757d;
  outline-offset: 1px;
  border-radius: 2px;
}

/* Responsive */
@media (max-width: 768px) {
  .compact-datatable :deep(.p-datatable-tbody > tr > td) {
    padding: 4px 6px;
    font-size: 0.8rem;
  }

  .compact-datatable :deep(.p-datatable-tbody > tr > td:first-child) {
    width: 35%;
  }

  .compact-datatable :deep(.p-datatable-tbody > tr > td:last-child) {
    width: 65%;
  }
}

@media (max-width: 480px) {
  .compact-datatable :deep(.p-datatable-tbody > tr > td) {
    padding: 3px 4px;
    font-size: 0.75rem;
  }
}
</style>
